import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './TenantDetail.css';
import { MaintenanceForm } from './Maintenance';
import ComplianceUpload from './ComplianceUpload';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime, timeAgo as fmtTimeAgo } from '../utils/dates';
import { usePagination, SeeMoreButton } from './usePagination';
import EditDetailsModal from './EditDetailsModal';
import { ArchiveButton, ArchivedBadge, RestoreButton } from './ArchiveControls';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_KEY = 'Bearer'; // Step 41 — auth via httpOnly cookie; this stub keeps existing headers harmless

const headers = { 'Authorization': API_KEY };
const jsonHeaders = { 'Authorization': API_KEY, 'Content-Type': 'application/json' };

function timeAgo(dateStr) {
  return fmtTimeAgo(dateStr, '');
}

// 2026-06-04 — Stat-strip card. Used at the top of TenantDetail to
// give the page some visual weight.
function StatCard({ label, value, sub, tone = '#0f172a', testid }) {
  return (
    <div data-testid={testid}
         style={{ background: '#fff',
                  borderRadius: 14, padding: '0.9rem 1.1rem',
                  borderTop: `3px solid ${tone}`,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.05), 0 4px 12px rgba(15,23,42,0.04)' }}>
      <div style={{ color: '#64748b', fontSize: '0.75rem',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ color: tone, fontSize: '1.5rem', fontWeight: 700,
                    marginTop: 4 }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

const DOC_TYPE_LABELS = {
  epc: 'EPC', gas_safety: 'Gas Safety', eicr: 'EICR',
};

const STATUS_BADGES = {
  active: { label: 'Active', cls: 'badge-green' },
  ended: { label: 'Ended', cls: 'badge-grey' },
  eviction_in_progress: { label: 'Eviction in Progress', cls: 'badge-red' },
};

const STAGE_LABELS = {
  1: 'Arrears Detection', 2: 'Threshold Check', 3: 'Notice Preparation',
  4: 'Notice Served', 5: 'Notice Period', 6: 'Start Court Proceedings',
  7: 'Hearing Preparation', 8: 'Outcome',
};

// 2026-02 — IMP 8: paginated Communications timeline. Renders 25
// interactions per "page" with a "+ See more" footer.
function CommunicationsList({ items, expandedIds, toggleExpand }) {
  const { visible, hasMore, remaining, showMore } = usePagination(items);
  if (!items || items.length === 0) {
    return <div className="empty-state"><p>No communications found</p></div>;
  }
  return (
    <>
      {visible.map(i => {
        const isExp = expandedIds.has(i.id);
        const preview = (i.content_text || '').substring(0, 100);
        const overflow = (i.content_text || '').length > 100;
        return (
          <div key={i.id}
               className={`tl-item ${i.flagged_for_review ? 'tl-flagged' : ''} ${i.direction === 'outbound' ? 'tl-outbound' : 'tl-inbound'}`}
               data-testid={`interaction-${i.id}`}>
            <div className="tl-header">
              <div className="tl-meta">
                <span className={`tl-channel-icon ${i.channel}`} data-testid={`channel-icon-${i.channel}`}>
                  {i.channel === 'whatsapp' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 8L2 4"/></svg>
                  )}
                </span>
                <span className="tl-direction">{i.direction === 'outbound' ? '\u2192' : '\u2190'}</span>
                <span className="tl-time">{formatDateTime(i.sent_at || i.created_at)} <span className="tl-relative">({fmtTimeAgo(i.sent_at || i.created_at, '')})</span></span>
              </div>
              <div className="tl-badges">
                {i.flagged_for_review && <span className="tl-flag-badge" data-testid="flag-badge">{i.flag_reason || 'Flagged'}</span>}
                {i.approved_by && <span className="tl-approved-badge" data-testid="approved-badge">Approved by {i.approved_by}</span>}
              </div>
            </div>
            {i.subject && <div className="tl-subject"><strong>Subject:</strong> {i.subject}</div>}
            <div className="tl-body">
              {isExp ? i.content_text : preview}
              {overflow && <button className="btn-expand" onClick={() => toggleExpand(i.id)}>{isExp ? 'Show less' : 'Show more'}</button>}
            </div>
          </div>
        );
      })}
      <SeeMoreButton hasMore={hasMore} remaining={remaining} onClick={showMore} testid="communications" />
    </>
  );
}

// 2026-02 — IMP 5: "Last AI Reply" preview card. Shown at the top of
// the Communications tab on TenantDetail. Lets the agent see exactly
// what the AI most recently sent + inline-edit the agency sign-off
// used by every future AI-drafted email.
function LastAiReplyCard({ lastAiReply, agentSignoff, onSignoffSaved }) {
  const [editing, setEditing] = useState(false);
  const [senderName, setSenderName] = useState((agentSignoff || {}).email_sender_name || '');
  const [signatureHtml, setSignatureHtml] = useState((agentSignoff || {}).email_signature_html || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSenderName((agentSignoff || {}).email_sender_name || '');
    setSignatureHtml((agentSignoff || {}).email_signature_html || '');
  }, [agentSignoff]);

  if (!lastAiReply) return null;

  const saveSignoff = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/agency/email-signoff`, {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({
          email_sender_name: senderName,
          email_signature_html: signatureHtml,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const next = await res.json();
      onSignoffSaved && onSignoffSaved(next);
      setEditing(false);
    } catch (e) {
      setError('Could not save sign-off. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const subject = lastAiReply.subject_line || lastAiReply.subject || '(no subject)';
  const sentAt = lastAiReply.sent_at || lastAiReply.created_at;
  const moduleName = lastAiReply.ai_module === 'maintenance_coordinator'
    ? 'Maintenance Coordinator' : 'Tenant AI';
  const previewText = (lastAiReply.content_text || '').trim();
  const previewHtml = lastAiReply.content_html || '';

  return (
    <div data-testid="last-ai-reply-card"
         style={{ background: '#fff', borderRadius: 14, padding: '1rem 1.1rem',
                  marginBottom: '1rem',
                  borderLeft: '3px solid #6366f1',
                  boxShadow: '0 1px 2px rgba(15,23,42,0.05), 0 4px 12px rgba(15,23,42,0.04)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: '1rem', marginBottom: '0.5rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ background: '#eef2ff', color: '#4338ca',
                           fontSize: '0.7rem', padding: '0.15rem 0.5rem',
                           borderRadius: 999, fontWeight: 600, textTransform: 'uppercase',
                           letterSpacing: '0.05em' }}
                  data-testid="last-ai-reply-badge">
              Last AI reply · {moduleName}
            </span>
            {sentAt && (
              <span style={{ color: '#64748b', fontSize: '0.78rem' }}
                    data-testid="last-ai-reply-time">
                {formatDateTime(sentAt)} ({timeAgo(sentAt)})
              </span>
            )}
          </div>
          <div style={{ marginTop: 6, fontWeight: 600, color: '#0f172a' }}
               data-testid="last-ai-reply-subject">
            {subject}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEditing(v => !v)}
          data-testid="btn-toggle-signoff-editor"
          style={{ background: editing ? '#e2e8f0' : '#0073EA',
                   color: editing ? '#0f172a' : '#fff',
                   border: 'none', borderRadius: 8,
                   padding: '0.4rem 0.75rem', fontSize: '0.8rem',
                   fontWeight: 600, cursor: 'pointer' }}>
          {editing ? 'Close' : 'Edit Sign-off'}
        </button>
      </div>
      {previewText ? (
        <div style={{ whiteSpace: 'pre-wrap', color: '#475569',
                      fontSize: '0.85rem', lineHeight: 1.5,
                      maxHeight: 160, overflow: 'auto',
                      background: '#f8fafc', padding: '0.6rem 0.75rem',
                      borderRadius: 8 }}
             data-testid="last-ai-reply-body">
          {previewText.length > 600 ? previewText.slice(0, 600) + '\u2026' : previewText}
        </div>
      ) : previewHtml ? (
        <div style={{ color: '#475569', fontSize: '0.85rem',
                      maxHeight: 160, overflow: 'auto', background: '#f8fafc',
                      padding: '0.6rem 0.75rem', borderRadius: 8 }}
             data-testid="last-ai-reply-body"
             dangerouslySetInnerHTML={{ __html: previewHtml }} />
      ) : null}

      {editing && (
        <div style={{ marginTop: '0.9rem', padding: '0.8rem 0.9rem',
                      background: '#fafafa', borderRadius: 8,
                      border: '1px solid #e5e7eb' }}
             data-testid="signoff-editor">
          <div style={{ fontSize: '0.78rem', color: '#475569', marginBottom: 6 }}>
            This sign-off appears at the bottom of every AI-drafted email.
            Updating it here applies to every future AI reply.
          </div>
          <label style={{ display: 'block', fontSize: '0.75rem',
                          color: '#334155', fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Sender Name
          </label>
          <input
            type="text"
            value={senderName}
            onChange={e => setSenderName(e.target.value)}
            placeholder="e.g. Sarah at Acme Lettings"
            data-testid="input-signoff-name"
            style={{ width: '100%', padding: '0.45rem 0.6rem', marginTop: 4,
                     border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.85rem' }}
          />
          <label style={{ display: 'block', fontSize: '0.75rem',
                          color: '#334155', fontWeight: 600, marginTop: 10,
                          textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            HTML Signature Block (optional)
          </label>
          <textarea
            value={signatureHtml}
            onChange={e => setSignatureHtml(e.target.value)}
            placeholder="<p>Kind regards,<br/>Sarah · Acme Lettings · 020 1234 5678</p>"
            data-testid="input-signoff-html"
            rows={4}
            style={{ width: '100%', padding: '0.45rem 0.6rem', marginTop: 4,
                     border: '1px solid #cbd5e1', borderRadius: 6,
                     fontSize: '0.8rem', fontFamily: 'ui-monospace, monospace' }}
          />
          {error && (
            <div data-testid="signoff-error"
                 style={{ color: '#b91c1c', fontSize: '0.8rem', marginTop: 6 }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: 10 }}>
            <button
              type="button"
              onClick={saveSignoff}
              disabled={saving}
              data-testid="btn-save-signoff"
              style={{ background: saving ? '#94a3b8' : '#0073EA',
                       color: '#fff', border: 'none', borderRadius: 6,
                       padding: '0.4rem 0.9rem', fontWeight: 600,
                       cursor: saving ? 'not-allowed' : 'pointer',
                       fontSize: '0.85rem' }}>
              {saving ? 'Saving\u2026' : 'Save sign-off'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              data-testid="btn-cancel-signoff"
              style={{ background: '#e2e8f0', color: '#0f172a',
                       border: 'none', borderRadius: 6,
                       padding: '0.4rem 0.9rem', fontWeight: 600,
                       cursor: 'pointer', fontSize: '0.85rem' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const TenantDetail = () => {
  const { tenant_id } = useParams();
  const navigate = useNavigate();

  // Default tab from URL query param
  const urlParams = new URLSearchParams(window.location.search);
  const defaultTab = urlParams.get('tab') || 'communications';
  // Item 0a refix (2026-02) — the alert deep-link already sets
  // activeTab='communications' via defaultTab above. But the
  // Communications section renders BELOW the header, RTR banner,
  // and stat cards (~500px of chrome), so landing on `?tab=…`
  // still visually opens on the "general/details" area unless we
  // scroll the thread into view. This ref is attached to the
  // Communications tab content; on mount + after data loads we
  // scroll it into view IF the URL asked for that tab. Runs once.
  const tabScrollTargetRef = React.useRef(null);
  const shouldScrollToTabRef = React.useRef(
    !!urlParams.get('tab'),
  );

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [data, setData] = useState(null);

  // Communications state
  const [filter, setFilter] = useState('all');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showComposer, setShowComposer] = useState(false);
  const [composerChannel, setComposerChannel] = useState('email');
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);
  // IMP 10 — Edit-tenant modal.
  const [editTenantOpen, setEditTenantOpen] = useState(false);

  // Tenancy edit state
  const [editingTenancy, setEditingTenancy] = useState(false);
  const [tenancyForm, setTenancyForm] = useState({});
  const [paymentForm, setPaymentForm] = useState({ payment_date: '', amount_paid: '' });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  // FIX 1/6 — Log Rent modal state. Single-popup with all fields visible
  // at once: rent_due_date, amount_due (pre-filled from tenancy.rent_amount,
  // editable), amount_paid (default 0), payment_date (optional).
  const [showLogRentModal, setShowLogRentModal] = useState(false);
  const [logRentForm, setLogRentForm] = useState({
    rent_due_date: '',
    amount_due: '',
    amount_paid: '0',
    payment_date: '',
  });
  const [loggingRent, setLoggingRent] = useState(false);
  const [logRentError, setLogRentError] = useState(null);
  const [logRentResult, setLogRentResult] = useState(null);

  // FIX 6 — Start Court Proceedings notice-period gate modal
  const [noticeGate, setNoticeGate] = useState({ open: false, notice_period_end: null, case_id: null });
  const [savingReminder, setSavingReminder] = useState(false);
  const [savingTenancy, setSavingTenancy] = useState(false);
  const [loggingPayment, setLoggingPayment] = useState(false);

  // Property landlord state
  const [allLandlords, setAllLandlords] = useState([]);
  const [propertyLandlord, setPropertyLandlord] = useState(null);
  const [savingLandlord, setSavingLandlord] = useState(false);

  // Property licensing state
  const [licensing, setLicensing] = useState(null);
  const [savingLicensing, setSavingLicensing] = useState(false);

  // Property availability state
  const [availability, setAvailability] = useState(null);
  const [availabilityInterests, setAvailabilityInterests] = useState([]);
  const [availabilityForm, setAvailabilityForm] = useState({
    availability_status: 'occupied',
    asking_rent: '',
    available_from: '',
    listing_description: '',
  });
  const [savingAvailability, setSavingAvailability] = useState(false);

  // Right to Rent state
  const [rtrData, setRtrData] = useState(null);
  const [showRtrForm, setShowRtrForm] = useState(false);
  const [rtrForm, setRtrForm] = useState({ right_to_rent_checked: false, right_to_rent_check_date: '', right_to_rent_expiry: '', right_to_rent_document_type: '' });
  const [savingRtr, setSavingRtr] = useState(false);

  // Maintenance state
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [showCreateTenancyForm, setShowCreateTenancyForm] = useState(false);

  // 2026-05-06 (Item 4 Part B) — properties summary for the new-agent
  // workflow guidance shown below the Create Tenancy button when the
  // tenant has no active tenancy. We need to know:
  //   • total property count       → "No properties found" message
  //   • count of properties with   → "Some properties have no linked
  //     no linked landlord_id        landlord" message
  // The summary endpoint already returns landlord_id per row.
  const [propsSummary, setPropsSummary] = useState({
    loaded: false, total: 0, withoutLandlord: 0,
  });

  // FIX 2 — Compliance upload modal hint (pre-fills doc type + property).
  const [complianceUploadHint, setComplianceUploadHint] = useState(null);

  // Deposit dispute state
  const [depositPrompt, setDepositPrompt] = useState(null);
  const [depositPartialMode, setDepositPartialMode] = useState(false);
  const [partialAmount, setPartialAmount] = useState('');
  const [partialReason, setPartialReason] = useState('');
  const [disputeData, setDisputeData] = useState(null);
  const [disputeUploading, setDisputeUploading] = useState(false);
  const [disputeAdvancing, setDisputeAdvancing] = useState(false);
  const [disputeOutcome, setDisputeOutcome] = useState('');
  const [disputeAmountAwarded, setDisputeAmountAwarded] = useState('');
  const [paymentError, setPaymentError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch');
      const d = await res.json();
      setData(d);
      if (d.tenancy) {
        setTenancyForm({
          rent_amount: d.tenancy.rent_amount || '',
          rent_frequency: d.tenancy.rent_frequency || 'monthly',
          rent_due_day: d.tenancy.rent_due_day || 1,
          deposit_amount: d.tenancy.deposit_amount || '',
          deposit_scheme: d.tenancy.deposit_scheme || '',
          deposit_certificate_served: d.tenancy.deposit_certificate_served || false,
        });
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tenant_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Item 0a refix (2026-02) — Scroll the Communications tab into
  // view when the page was opened via an alert deep-link
  // (`?tab=communications`). Without this, the tab activates
  // correctly but the tenant header + RTR banner + stat cards
  // (all above the tab) push the conversation off-screen, so the
  // page looks like it landed on the "details" area. Runs once
  // after the data-load resolves so the target element exists.
  useEffect(() => {
    if (!data) return;
    if (!shouldScrollToTabRef.current) return;
    if (activeTab !== 'communications') return;
    // Defer to the next paint so refs are attached and the tab
    // content is in the DOM.
    const raf = requestAnimationFrame(() => {
      const el = tabScrollTargetRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      shouldScrollToTabRef.current = false;
    });
    return () => cancelAnimationFrame(raf);
  }, [data, activeTab]);

  // Fetch landlord list and property-linked landlord
  useEffect(() => {
    const fetchLandlords = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/landlords`, { headers });
        if (res.ok) setAllLandlords(await res.json());
      } catch {}
    };
    fetchLandlords();
  }, []);

  useEffect(() => {
    if (!data?.property?._id) return;
    const fetchPropertyLandlord = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/properties/${data.property._id}/landlord`, { headers });
        if (res.ok) {
          const d = await res.json();
          setPropertyLandlord(d.landlord);
        }
      } catch {}
    };
    const fetchPropertyLicensing = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/properties/${data.property._id}/licensing`, { headers });
        if (res.ok) setLicensing(await res.json());
      } catch {}
    };
    const fetchPropertyAvailability = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/properties/${data.property._id}/availability`, { headers });
        if (res.ok) {
          const text = await res.text();
          const d = text ? JSON.parse(text) : null;
          if (d) {
            setAvailability(d);
            setAvailabilityForm({
              availability_status: d.availability_status || 'occupied',
              asking_rent: d.asking_rent != null ? String(d.asking_rent) : '',
              available_from: d.available_from || '',
              listing_description: d.listing_description || '',
            });
          }
        }
      } catch {}
    };
    const fetchAvailabilityInterests = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/properties/${data.property._id}/availability-interests`, { headers });
        if (res.ok) {
          const text = await res.text();
          setAvailabilityInterests(text ? JSON.parse(text) : []);
        }
      } catch {}
    };
    fetchPropertyLandlord();
    fetchPropertyLicensing();
    fetchPropertyAvailability();
    fetchAvailabilityInterests();
  }, [data?.property?._id]);

  // Fetch Right to Rent status
  useEffect(() => {
    if (!tenant_id) return;
    const fetchRtr = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/compliance/right-to-rent/${tenant_id}`, { headers });
        if (res.ok) {
          const d = await res.json();
          setRtrData(d);
          setRtrForm({
            right_to_rent_checked: d.right_to_rent_checked || false,
            right_to_rent_check_date: d.right_to_rent_check_date || '',
            right_to_rent_expiry: d.right_to_rent_expiry || '',
            right_to_rent_document_type: d.right_to_rent_document_type || '',
          });
        }
      } catch {}
    };
    fetchRtr();
  }, [tenant_id, data]);

  // Fetch deposit dispute data when tenancy loads
  useEffect(() => {
    if (!data?.tenancy) return;
    const tid = data.tenancy.id || data.tenancy._id;
    if (!tid) return;
    const status = data.tenancy.deposit_dispute_status;
    if (status && status !== 'none') {
      fetchDisputeData();
    }
  }, [data?.tenancy?.deposit_dispute_status]);

  // 2026-05-06 (Item 4 Part B) — Once we know the tenant has no active
  // tenancy, fetch the property/landlord state so we can render the
  // correct guidance message below the Create Tenancy button. Skipped
  // entirely while the tenant data is still loading or already has a
  // tenancy (the guidance only applies to no-tenancy tenants).
  useEffect(() => {
    if (!data) return;
    if (data.tenancy) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/properties/list-with-summary`, { headers });
        const txt = await res.text();
        const list = txt ? JSON.parse(txt) : [];
        if (cancelled || !res.ok || !Array.isArray(list)) return;
        const total = list.length;
        const withoutLandlord = list.filter(p => !p.landlord_id).length;
        setPropsSummary({ loaded: true, total, withoutLandlord });
      } catch {
        if (!cancelled) setPropsSummary({ loaded: true, total: 0, withoutLandlord: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [data?.tenancy]);

  const handleLandlordChange = async (landlordId) => {
    setSavingLandlord(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/properties/${data.property._id}/landlord`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ landlord_id: landlordId || null })
      });
      if (res.ok) {
        const d = await res.json();
        if (d.landlord_id) {
          const ll = allLandlords.find(l => l._id === d.landlord_id);
          setPropertyLandlord(ll || null);
        } else {
          setPropertyLandlord(null);
        }
      }
    } catch (err) {
      alert('Failed to update landlord: ' + err.message);
    } finally {
      setSavingLandlord(false);
    }
  };

  const updateLicensingField = async (field, value) => {
    const updated = { ...licensing, [field]: value };
    setLicensing(updated);
    setSavingLicensing(true);
    try {
      await fetch(`${API_BASE_URL}/api/properties/${data.property._id}/licensing`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ [field]: value })
      });
    } catch (err) {
      console.error('Failed to save licensing field:', err);
    } finally {
      setSavingLicensing(false);
    }
  };

  const saveAvailability = async () => {
    if (!data?.property?._id) return;
    setSavingAvailability(true);
    try {
      const body = {
        availability_status: availabilityForm.availability_status,
        asking_rent: availabilityForm.asking_rent === '' ? null : parseFloat(availabilityForm.asking_rent),
        available_from: availabilityForm.available_from || '',
        listing_description: availabilityForm.listing_description || '',
      };
      const res = await fetch(`${API_BASE_URL}/api/properties/${data.property._id}/availability`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify(body),
      });
      const text = await res.text();
      const d = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error((d && d.detail) || 'Failed to save availability');
      if (d) {
        setAvailability(d);
        setAvailabilityForm({
          availability_status: d.availability_status || 'occupied',
          asking_rent: d.asking_rent != null ? String(d.asking_rent) : '',
          available_from: d.available_from || '',
          listing_description: d.listing_description || '',
        });
      }
    } catch (err) {
      alert('Failed to save availability: ' + err.message);
    } finally {
      setSavingAvailability(false);
    }
  };

  const getLicenceStatus = (expiry) => {
    if (!expiry) return { label: 'Not Licensed', cls: 'lic-grey' };
    const exp = new Date(expiry);
    const now = new Date();
    const days = Math.floor((exp - now) / 86400000);
    if (days < 0) return { label: 'Expired', cls: 'lic-red' };
    if (days <= 45) return { label: `Expiring (${days}d)`, cls: days <= 15 ? 'lic-red' : 'lic-amber' };
    return { label: 'Licensed', cls: 'lic-green' };
  };

  if (loading) return <div className="td-container"><div className="td-loading">Loading tenant details...</div></div>;
  if (!data?.tenant) return (
    <div className="td-container">
      <div className="td-error">Tenant not found</div>
      <button className="btn-back" onClick={() => navigate('/')}>Back to Dashboard</button>
    </div>
  );

  const { tenant, tenancy, property, active_case, arrears_balance, months_in_arrears, interactions, arrears_records, compliance_docs, case_transitions, joint_tenants } = data;
  const rentAmount = tenancy?.rent_amount || 0;
  const arrearsGte2Months = rentAmount > 0 && arrears_balance >= rentAmount * 2;
  const tenancyStatus = active_case ? 'eviction_in_progress' : (tenancy?.status || 'ended');
  const badge = STATUS_BADGES[tenancyStatus] || STATUS_BADGES.ended;
  const address = property ? `${property.address_line1}${property.address_line2 ? ', ' + property.address_line2 : ''}, ${property.city}, ${property.postcode}` : 'No property';

  // Filter interactions
  const filtered = (interactions || []).filter(i => {
    if (filter === 'all') return true;
    if (filter === 'whatsapp' || filter === 'email') return i.channel === filter;
    if (filter === 'inbound') return i.direction === 'inbound';
    if (filter === 'outbound') return i.direction === 'outbound';
    if (filter === 'flagged') return i.flagged_for_review;
    return true;
  });

  const toggleExpand = id => setExpandedIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // Send message
  const handleSend = async () => {
    if (!msgBody.trim()) return;
    if (composerChannel === 'email' && !msgSubject.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/send-message`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ channel: composerChannel, subject: composerChannel === 'email' ? msgSubject : undefined, body: msgBody }),
      });
      const d = await res.json();
      if (d.success) {
        setMsgSubject(''); setMsgBody(''); setShowComposer(false);
        fetchData();
      }
    } catch {} finally { setSending(false); }
  };

  // Save tenancy edits
  const handleSaveTenancy = async () => {
    setSavingTenancy(true);
    try {
      await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/tenancy`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify(tenancyForm),
      });
      setEditingTenancy(false);
      fetchData();
    } catch {} finally { setSavingTenancy(false); }
  };

  // Log payment
  const handleLogPayment = async () => {
    if (!paymentForm.payment_date || !paymentForm.amount_paid) return;
    setLoggingPayment(true);
    setPaymentError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/log-payment`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ payment_date: paymentForm.payment_date, amount_paid: parseFloat(paymentForm.amount_paid) }),
      });
      const text = await res.text();
      const body = text ? JSON.parse(text) : {};
      if (!res.ok || body.success === false) {
        setPaymentError(body.detail || body.message || `Log payment failed (HTTP ${res.status})`);
        return;
      }
      // Success — reset form + refresh tenant-level data (arrears_balance,
      // months_in_arrears, arrears_records) so header, banner, and Arrears
      // History table all re-render from the fresh backend response.
      setPaymentForm({ payment_date: '', amount_paid: '' });
      setShowPaymentForm(false);
      await fetchData();
      // If payment brought arrears back under the S8 threshold the backend
      // auto-dismisses s8_threshold alerts — refresh the alert panel instantly.
      window.dispatchEvent(new Event('eiq-alerts-refresh'));
    } catch (err) {
      setPaymentError(err.message || 'Network error — please try again.');
    } finally { setLoggingPayment(false); }
  };

  // FIX 4 — Submit a single rent period
  const handleLogRent = async () => {
    setLogRentError(null);
    setLogRentResult(null);
    if (!logRentForm.rent_due_date) {
      setLogRentError('Please select the rent due date.');
      return;
    }
    if (logRentForm.amount_due === '' || isNaN(parseFloat(logRentForm.amount_due))) {
      setLogRentError('Please enter the amount due.');
      return;
    }
    setLoggingRent(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/tenants/${tenant_id}/log-rent-period`,
        {
          method: 'POST', headers: jsonHeaders,
          body: JSON.stringify({
            rent_due_date: logRentForm.rent_due_date,
            amount_due: parseFloat(logRentForm.amount_due),
            amount_paid: parseFloat(logRentForm.amount_paid || '0'),
            payment_date: logRentForm.payment_date || null,
          }),
        });
      const text = await res.text();
      const body = text ? JSON.parse(text) : {};
      if (!res.ok || body.success === false) {
        setLogRentError(body.detail || body.message || `Log rent failed (HTTP ${res.status})`);
        return;
      }
      setLogRentResult(body);
      // Re-fetch tenant data so header balance + arrears table re-render.
      await fetchData();
      window.dispatchEvent(new Event('eiq-alerts-refresh'));
    } catch (err) {
      setLogRentError(err.message || 'Network error — please try again.');
    } finally {
      setLoggingRent(false);
    }
  };

  // End tenancy — check for deposit dispute prompt
  const handleEndTenancy = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/end-tenancy`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ check_deposit: true }),
      });
      const text = await res.text();
      const d = text ? JSON.parse(text) : {};
      if (d.has_deposit) {
        setDepositPrompt(d);
      } else {
        if (window.confirm('Are you sure you want to end this tenancy?')) {
          await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/end-tenancy`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({}) });
          fetchData();
        }
      }
    } catch {}
  };

  const handleEndNoDispute = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/end-tenancy`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ deposit_outcome: 'full_return' }) });
      setDepositPrompt(null);
      setDepositPartialMode(false);
      fetchData();
    } catch {}
  };

  // FIX 4 (2026-05-05) — Partial-deduction option records the amount and
  // reason on the tenancy before ending it.
  const handleEndWithPartial = async () => {
    if (!partialAmount || isNaN(parseFloat(partialAmount))) {
      window.alert('Enter the amount retained.');
      return;
    }
    if (!partialReason.trim()) {
      window.alert('Provide a reason for the deduction.');
      return;
    }
    try {
      await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/end-tenancy`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({
          deposit_outcome: 'partial_deduction',
          deposit_partial_amount: parseFloat(partialAmount),
          deposit_partial_reason: partialReason.trim(),
        }),
      });
      setDepositPrompt(null);
      setDepositPartialMode(false);
      setPartialAmount('');
      setPartialReason('');
      fetchData();
    } catch {}
  };

  const handleEndWithDispute = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/tenants/${tenant_id}/end-tenancy`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ dispute: true, deposit_outcome: 'dispute' }) });
      setDepositPrompt(null);
      setDepositPartialMode(false);
      fetchData();
      fetchDisputeData();
    } catch {}
  };

  // Fetch dispute data
  const fetchDisputeData = async () => {
    if (!data?.tenancy) return;
    const tid = data.tenancy.id || data.tenancy._id;
    try {
      const res = await fetch(`${API_BASE_URL}/api/deposit-dispute/${tid}`, { headers });
      if (res.ok) {
        const text = await res.text();
        setDisputeData(text ? JSON.parse(text) : null);
      }
    } catch {}
  };

  // Advance dispute stage
  const handleAdvanceDispute = async () => {
    if (!disputeData) return;
    setDisputeAdvancing(true);
    try {
      const body = {};
      if (disputeData.status === 'adjudicating') {
        body.outcome = disputeOutcome;
        body.amount_awarded = disputeAmountAwarded ? parseFloat(disputeAmountAwarded) : null;
      }
      const res = await fetch(`${API_BASE_URL}/api/deposit-dispute/${disputeData.tenancy_id}/advance`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify(body),
      });
      if (res.ok) { fetchDisputeData(); setDisputeOutcome(''); setDisputeAmountAwarded(''); }
    } catch {} finally { setDisputeAdvancing(false); }
  };

  // Upload dispute docs
  const handleDisputeUpload = async (files) => {
    if (!disputeData || !files.length) return;
    setDisputeUploading(true);
    try {
      const fd = new FormData();
      [...files].forEach(f => fd.append('files', f));
      await fetch(`${API_BASE_URL}/api/deposit-dispute/${disputeData.tenancy_id}/upload`, {
        method: 'POST', headers: { 'Authorization': API_KEY }, body: fd,
      });
      fetchDisputeData();
    } catch {} finally { setDisputeUploading(false); }
  };

  // Save Right to Rent
  const handleSaveRtr = async () => {
    setSavingRtr(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/compliance/right-to-rent`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ tenant_id, ...rtrForm }),
      });
      if (res.ok) {
        setShowRtrForm(false);
        const rRes = await fetch(`${API_BASE_URL}/api/compliance/right-to-rent/${tenant_id}`, { headers });
        if (rRes.ok) setRtrData(await rRes.json());
      }
    } catch {} finally { setSavingRtr(false); }
  };

  // Case tab helper
  const hasCase = !!active_case;
  const caseStage = active_case?.current_stage || 0;

  return (
    <div className="td-container" data-testid="tenant-detail-page">
      {/* Breadcrumb */}
      <nav className="td-breadcrumb" data-testid="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span className="bc-sep">/</span>
        <span>Tenants</span>
        <span className="bc-sep">/</span>
        <span className="bc-current">{tenant.first_name} {tenant.last_name}</span>
      </nav>

      {/* IMP 10 — Edit tenant details modal */}
      <EditDetailsModal
        open={editTenantOpen}
        title="Edit tenant details"
        endpoint={`${API_BASE_URL}/api/tenants/${tenant_id}/details`}
        initial={{
          first_name: tenant.first_name,
          last_name: tenant.last_name,
          email: tenant.email,
          phone: tenant.phone_number || tenant.phone,
          date_of_birth: tenant.date_of_birth ? String(tenant.date_of_birth).slice(0, 10) : '',
          national_insurance_number: tenant.national_insurance_number,
          right_to_rent_status: tenant.right_to_rent_status,
          right_to_rent_expiry: tenant.right_to_rent_expiry ? String(tenant.right_to_rent_expiry).slice(0, 10) : '',
          emergency_contact_name: tenant.emergency_contact_name,
          emergency_contact_phone: tenant.emergency_contact_phone,
          notes: tenant.notes,
        }}
        fields={[
          { key: 'first_name', label: 'First name' },
          { key: 'last_name', label: 'Last name' },
          { key: 'email', label: 'Email', type: 'email' },
          { key: 'phone', label: 'Phone' },
          { key: 'date_of_birth', label: 'Date of birth', type: 'date' },
          { key: 'national_insurance_number', label: 'NI number' },
          { key: 'right_to_rent_status', label: 'Right to Rent', type: 'select',
            options: [
              { value: 'unlimited', label: 'Unlimited' },
              { value: 'time_limited', label: 'Time-limited' },
              { value: 'not_checked', label: 'Not checked' },
              { value: 'failed', label: 'Failed' },
            ] },
          { key: 'right_to_rent_expiry', label: 'RTR expiry', type: 'date' },
          { key: 'emergency_contact_name', label: 'Emergency contact' },
          { key: 'emergency_contact_phone', label: 'Emergency phone' },
          { key: 'notes', label: 'Internal notes', type: 'textarea', full: true, rows: 3 },
        ]}
        onClose={() => setEditTenantOpen(false)}
        onSaved={() => window.location.reload()}
      />

      {/* Header */}
      <header className="td-header" data-testid="tenant-header">
        <div className="td-header-main">
          <h1 data-testid="tenant-name">{tenant.first_name} {tenant.last_name}</h1>
          <span className={`td-status-badge ${badge.cls}`} data-testid="tenancy-status-badge">{badge.label}</span>
          <button
            type="button"
            onClick={() => setEditTenantOpen(true)}
            data-testid="btn-edit-tenant"
            style={{ marginLeft: 12, background: '#0073EA',
                     color: '#fff', border: 'none', borderRadius: 6,
                     padding: '0.4rem 0.85rem', fontWeight: 600,
                     fontSize: '0.82rem', cursor: 'pointer' }}>
            Edit details
          </button>
          {tenant.archived ? (
            <span style={{ marginLeft: 8, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <ArchivedBadge />
              <RestoreButton kind="tenant" id={tenant_id}
                              onRestored={() => window.location.reload()} />
            </span>
          ) : (
            <span style={{ marginLeft: 8 }}>
              <ArchiveButton kind="tenant" id={tenant_id}
                              testid="btn-archive-tenant"
                              onArchived={() => navigate('/')} />
            </span>
          )}
        </div>
        <div className="td-header-meta">
          <span data-testid="tenant-email"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 8L2 4"/></svg> {tenant.email || 'No email'}</span>
          <span data-testid="tenant-phone"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg> {tenant.phone_number || 'No phone'}</span>
          <span data-testid="tenant-address"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> {address}</span>
        </div>
        {tenancy && (
          <div className="td-header-finance">
            <span>Rent: <strong>{tenancy.rent_amount ? formatCurrency(tenancy.rent_amount) : 'N/A'}</strong> / {tenancy.rent_frequency || 'month'}</span>
            <span className={arrears_balance > 0 ? (arrearsGte2Months ? 'arrears-critical' : 'arrears-warning') : ''} data-testid="arrears-balance">
              Arrears: <strong>{arrears_balance > 0 ? formatCurrency(arrears_balance) : formatCurrency(0)}</strong>
              {arrearsGte2Months && <span className="arrears-months-badge">{months_in_arrears.toFixed(1)} months</span>}
            </span>
          </div>
        )}
        {/* Joint tenants */}
        {joint_tenants && joint_tenants.length > 1 && (
          <div className="td-joint-tenants" data-testid="joint-tenants">
            <span className="jt-label">Joint tenancy:</span>
            {joint_tenants.map(jt => (
              <span key={jt.id || jt._id} className={`jt-name ${jt.is_lead ? 'jt-lead' : ''}`}>
                {jt.first_name} {jt.last_name}{jt.is_lead ? ' (Lead)' : ''}
              </span>
            ))}
          </div>
        )}
        {/* Quick actions */}
        <div className="td-quick-actions" data-testid="quick-actions">
          {/* 2026-05-30 — WhatsApp quick action removed per user request.
              Outbound triage / chase still uses email + activity log. */}
          <button className="btn-action btn-email" onClick={() => { setComposerChannel('email'); setShowComposer(true); }} data-testid="btn-quick-email">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 8L2 4"/></svg>
            Email
          </button>
          <button
            className="btn-action btn-pay"
            onClick={() => {
              setActiveTab('tenancy');
              setShowLogRentModal(true);
            }}
            data-testid="btn-quick-log-rent">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 7c0-5.333-8-5.333-8 0"/><path d="M10 7v14"/><path d="M6 21h12"/><path d="M6 13h10"/></svg>
            Log Rent
          </button>
          {hasCase ? (
            <Link to={`/section8/case/${active_case.id || active_case._id}`} className="btn-action btn-case" data-testid="btn-quick-case">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Open Case
            </Link>
          ) : tenancy && (
            <Link to={`/section8/${tenancy.id || tenancy._id}`} className="btn-action btn-start-s8" data-testid="btn-quick-start-s8">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Start Section 8 Process
            </Link>
          )}
          {tenancy && (
            <button className="btn-action btn-maint" onClick={() => setShowMaintForm(true)} data-testid="btn-quick-maintenance">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
              Log Maintenance
            </button>
          )}
          {/* Step 52 BUG 2 — Create Tenancy CTA. Visible whenever the tenant
              has no active tenancy (typical for manually-added tenants).
              Step 53 BUG 2 — Use the same .btn-action style as the other
              action-bar buttons so it sits inline at consistent size. */}
          {!tenancy && (
            <button
              type="button"
              onClick={() => setShowCreateTenancyForm(true)}
              data-testid="btn-create-tenancy"
              className="btn-action btn-create-tenancy"
              style={{
                background: '#0073EA', color: '#fff', border: 'none',
                padding: '0.5rem 0.9rem', borderRadius: 6,
                fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer',
              }}>
              + Create Tenancy
            </button>
          )}
        </div>
        {/* 2026-05-06 (Item 4 Part B) — Workflow guidance under the
            Create Tenancy button. Only renders when the tenant has no
            active tenancy and we've finished loading the property
            summary. Two mutually-exclusive variants:
              • no properties at all  → cannot create a tenancy yet
              • some unlinked         → S8 is blocked without a landlord */}
        {!tenancy && propsSummary.loaded && propsSummary.total === 0 && (
          <div
            data-testid="td-no-properties-msg"
            style={{
              background: '#fffbeb', border: '1px solid #fcd34d',
              color: '#92400e', borderRadius: 8,
              padding: '0.7rem 1rem', margin: '0.6rem 0 0',
              fontSize: '0.88rem', lineHeight: 1.5, maxWidth: 720,
            }}>
            No properties found. Add a property and link it to a
            landlord before creating a tenancy.
          </div>
        )}
        {!tenancy && propsSummary.loaded
            && propsSummary.total > 0
            && propsSummary.withoutLandlord > 0 && (
          <div
            data-testid="td-unlinked-landlord-msg"
            style={{
              background: '#fffbeb', border: '1px solid #fcd34d',
              color: '#92400e', borderRadius: 8,
              padding: '0.7rem 1rem', margin: '0.6rem 0 0',
              fontSize: '0.88rem', lineHeight: 1.5, maxWidth: 720,
            }}>
            Some properties have no linked landlord. A Section 8 notice
            cannot be generated without a landlord. Link landlords in{' '}
            <a href="/?view=settings&tab=landlords"
               style={{ color: '#7c2d12', fontWeight: 600 }}
               data-testid="td-link-settings-landlords">
              Settings &gt; Landlords
            </a>.
          </div>
        )}
        {showCreateTenancyForm && !tenancy && (
          <CreateTenancyModal
            tenantId={tenant.id || tenant._id}
            apiBase={API_BASE_URL}
            onClose={() => setShowCreateTenancyForm(false)}
            onCreated={() => { setShowCreateTenancyForm(false); fetchData(); }}
          />
        )}
        {showMaintForm && tenancy && (
          <MaintenanceForm
            tenancies={[{ id: tenancy.id || tenancy._id, tenant_name: `${tenant.first_name} ${tenant.last_name}`, property_address: address }]}
            defaultTenancyId={tenancy.id || tenancy._id}
            onClose={() => setShowMaintForm(false)}
            onCreated={() => { setShowMaintForm(false); }}
          />
        )}
      </header>

      {/* Deposit Dispute Prompt Modal */}
      {depositPrompt && (
        <div className="dd-prompt-overlay" data-testid="deposit-prompt">
          <div className="dd-prompt-panel">
            <h3>Deposit Dispute Check</h3>
            <p>This tenancy has a deposit of <strong>{formatCurrency(depositPrompt.deposit_amount)}</strong>{depositPrompt.deposit_scheme ? ` held with ${depositPrompt.deposit_scheme}` : ''}. Before ending this tenancy please confirm how the deposit will be handled.</p>
            {!depositPartialMode ? (
              <>
                <div className="dd-prompt-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button className="btn-secondary" onClick={handleEndNoDispute} data-testid="btn-deposit-full-return">
                    Full deposit returned to tenant — no dispute
                  </button>
                  <button className="btn-secondary" onClick={() => setDepositPartialMode(true)} data-testid="btn-deposit-partial">
                    Partial deduction agreed — enter amount retained and reason
                  </button>
                  <button className="btn-danger" style={{ background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer' }} onClick={handleEndWithDispute} data-testid="btn-deposit-dispute">
                    Deposit dispute — tenant has disputed the deductions
                  </button>
                </div>
                <button className="btn-text" onClick={() => setDepositPrompt(null)} data-testid="btn-cancel-end">Cancel</button>
              </>
            ) : (
              <>
                <div data-testid="deposit-partial-form" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
                  <label style={{ fontSize: '0.85rem', color: '#475569' }}>
                    Amount retained (£)
                    <input type="number" step="0.01" min="0"
                           value={partialAmount}
                           onChange={e => setPartialAmount(e.target.value)}
                           data-testid="dd-partial-amount"
                           style={{ display: 'block', width: '100%', marginTop: 4,
                                    padding: '0.5rem',
                                    border: '1px solid #cbd5e1', borderRadius: 4 }} />
                  </label>
                  <label style={{ fontSize: '0.85rem', color: '#475569' }}>
                    Reason for deduction
                    <textarea rows="3"
                              value={partialReason}
                              onChange={e => setPartialReason(e.target.value)}
                              data-testid="dd-partial-reason"
                              style={{ display: 'block', width: '100%', marginTop: 4,
                                       padding: '0.5rem',
                                       border: '1px solid #cbd5e1', borderRadius: 4,
                                       resize: 'vertical' }} />
                  </label>
                </div>
                <div className="dd-prompt-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <button className="btn-secondary" onClick={() => setDepositPartialMode(false)} data-testid="btn-deposit-partial-back">
                    Back
                  </button>
                  <button className="btn-primary" onClick={handleEndWithPartial} data-testid="btn-deposit-partial-confirm"
                          style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.5rem 1.1rem', borderRadius: 6, cursor: 'pointer' }}>
                    Confirm partial deduction & end tenancy
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Right to Rent — prominent section above tabs */}
      <div className={`td-rtr-section rtr-${rtrData?.status || 'red'}`} data-testid="rtr-section">
        <div className="rtr-row">
          <div className="rtr-info">
            <span className="rtr-label">Right to Rent</span>
            <span className={`rtr-badge rtr-badge-${rtrData?.status || 'red'}`} data-testid="rtr-badge">
              {rtrData?.status_text || 'Not checked'}
            </span>
            {rtrData?.right_to_rent_document_type && (
              <span className="rtr-doc-type">{rtrData.right_to_rent_document_type}</span>
            )}
            {rtrData?.right_to_rent_check_date && (
              <span className="rtr-check-date">Checked: {rtrData.right_to_rent_check_date}</span>
            )}
            {rtrData?.right_to_rent_expiry && (
              <span className="rtr-expiry">Expires: {rtrData.right_to_rent_expiry}</span>
            )}
          </div>
          <button className="btn-sm btn-secondary" onClick={() => setShowRtrForm(!showRtrForm)} data-testid="btn-update-rtr">
            {showRtrForm ? 'Cancel' : 'Update Right to Rent'}
          </button>
        </div>
        {showRtrForm && (
          <div className="rtr-form" data-testid="rtr-form">
            <label className="rtr-check-label">
              <input type="checkbox" checked={rtrForm.right_to_rent_checked} onChange={e => setRtrForm(f => ({ ...f, right_to_rent_checked: e.target.checked }))} data-testid="rtr-checked" />
              Check completed
            </label>
            <div className="rtr-form-fields">
              <div className="rtr-field">
                <label>Check Date</label>
                <input type="date" value={rtrForm.right_to_rent_check_date} onChange={e => setRtrForm(f => ({ ...f, right_to_rent_check_date: e.target.value }))} data-testid="rtr-check-date" />
              </div>
              <div className="rtr-field">
                <label>Document Type</label>
                <select value={rtrForm.right_to_rent_document_type} onChange={e => setRtrForm(f => ({ ...f, right_to_rent_document_type: e.target.value }))} data-testid="rtr-doc-type">
                  <option value="">-- Select --</option>
                  <option value="passport">Passport</option>
                  <option value="visa">Visa</option>
                  <option value="biometric_residence_permit">Biometric Residence Permit</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="rtr-field">
                <label>Expiry Date <span className="rtr-hint">(leave blank for indefinite/British citizen)</span></label>
                <input type="date" value={rtrForm.right_to_rent_expiry} onChange={e => setRtrForm(f => ({ ...f, right_to_rent_expiry: e.target.value }))} data-testid="rtr-expiry" />
              </div>
            </div>
            <button
              className="btn-sm"
              style={{
                background: savingRtr ? '#94a3b8' : '#0073EA',
                color: '#fff', border: 'none',
                cursor: savingRtr ? 'not-allowed' : 'pointer',
              }}
              onClick={handleSaveRtr}
              disabled={savingRtr}
              data-testid="btn-save-rtr">
              {savingRtr ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* FIX 3 — Empty-state banner when the tenant has no active tenancy
          or no linked property. Replaces all empty sections below until
          a tenancy is created. */}
      {!tenancy && (
        <div data-testid="no-tenancy-banner"
             style={{ background: '#fff7ed', border: '1px solid #fdba74',
                      borderRadius: 10, padding: '1.1rem 1.4rem',
                      margin: '1rem 0', color: '#9a3412' }}>
          <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.05rem',
                       color: '#7c2d12' }}>
            Tenant not yet linked to a property
          </h3>
          <p style={{ margin: '0 0 1rem', fontSize: '0.92rem' }}>
            This tenant is not yet linked to a property or tenancy. To view
            compliance documents, arrears history, and tenancy details you
            need to create a tenancy.
          </p>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setShowCreateTenancyForm(true)}
              data-testid="btn-no-tenancy-create"
              style={{ background: '#0073EA', color: '#fff', border: 'none',
                       padding: '0.55rem 1.1rem', borderRadius: 6,
                       fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer' }}>
              Create Tenancy
            </button>
            <button
              type="button"
              onClick={() => {
                // 2026-05-06 (Item 3) — Routing fix. If the tenant has a
                // linked property go straight to that property's detail
                // page; otherwise fall back to the Properties list. The
                // previous behaviour navigated to '/properties' which the
                // App's currentView routing surfaced as the dashboard.
                if (property && (property._id || property.id)) {
                  navigate(`/properties/${property._id || property.id}`);
                } else {
                  navigate('/?view=properties');
                }
              }}
              data-testid="btn-no-tenancy-go-properties"
              style={{ background: '#fff', color: '#9a3412',
                       border: '1px solid #9a3412',
                       padding: '0.55rem 1.1rem', borderRadius: 6,
                       fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer' }}>
              Go to Properties
            </button>
          </div>
        </div>
      )}

      {/* 2026-06-04 — Tenant summary stat strip. Adds visual depth so
          the tenant page no longer looks bare. */}
      {tenancy && (
        <div className="td-stat-strip" data-testid="tenant-stat-strip"
             style={{ display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: 14, margin: '1rem 0 1.2rem' }}>
          <StatCard label="Monthly rent"
            value={tenancy.rent_amount ? formatCurrency(tenancy.rent_amount) : '—'}
            sub={tenancy.rent_frequency || 'month'}
            tone="#0073EA" testid="stat-monthly-rent" />
          <StatCard label="Arrears balance"
            value={formatCurrency(arrears_balance || 0)}
            sub={arrearsGte2Months
              ? `${months_in_arrears.toFixed(1)} months — S8 threshold`
              : (arrears_balance > 0 ? `${months_in_arrears.toFixed(1)} months` : 'No arrears')}
            tone={arrears_balance > 0 ? (arrearsGte2Months ? '#dc2626' : '#d97706') : '#16a34a'}
            testid="stat-arrears-balance" />
          <StatCard label="Payments logged"
            value={String((arrears_records || []).filter(r => (r.amount_paid || 0) > 0).length)}
            sub={`${(interactions || []).length} interactions`}
            tone="#7c3aed" testid="stat-payments-logged" />
          <StatCard label="Compliance docs"
            value={String((compliance_docs || []).filter(d => d.is_current && DOC_TYPE_LABELS[d.doc_type]).length)}
            sub={`${(compliance_docs || []).filter(d => !d.is_current && DOC_TYPE_LABELS[d.doc_type]).length} archived`}
            tone="#0891b2" testid="stat-compliance-docs" />
        </div>
      )}

      {/* Tabs */}
      {tenancy && <div className="td-tabs" data-testid="tenant-tabs">
        {['communications', 'tenancy', 'case', 'compliance'].map(tab => (
          <button
            key={tab}
            className={`td-tab ${activeTab === tab ? 'active' : ''} ${tab === 'case' && !hasCase ? 'td-tab-hidden' : ''}`}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab}`}
            style={tab === 'case' && !hasCase ? { display: 'none' } : {}}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>}

      {/* TAB 1: Communications */}
      {tenancy && activeTab === 'communications' && (
        <div className="td-tab-content" data-testid="communications-tab" ref={tabScrollTargetRef}>
          <LastAiReplyCard
            lastAiReply={data.last_ai_reply}
            agentSignoff={data.agent_signoff}
            onSignoffSaved={(next) => {
              setData(prev => prev ? { ...prev, agent_signoff: next } : prev);
            }}
          />
          <div className="filter-bar">
            <div className="filter-buttons">
              {/* 2026-05-31 — 'whatsapp' filter removed per user request. */}
              {['all', 'email', 'inbound', 'outbound', 'flagged'].map(f => (
                <button key={f} className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <button className="btn-send-message" style={{ background: '#0073EA', color: '#fff', border: 'none', cursor: 'pointer' }} onClick={() => setShowComposer(true)} data-testid="btn-send-message">Send Message</button>
          </div>
          <div className="timeline" data-testid="communications-timeline">
            <CommunicationsList
              items={filtered}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
            />
          </div>
        </div>
      )}

      {/* TAB 2: Tenancy */}
      {tenancy && activeTab === 'tenancy' && (
        <div className="td-tab-content" data-testid="tenancy-tab">
          {!tenancy ? <div className="empty-state"><p>No active tenancy</p></div> : (
            <>
              {/* Arrears Guidance Banner — content driven by months-in-arrears
                  and agent legislation regime (pre_may_2026 = 2mo Ground 8,
                  post_may_2026 = 3mo Ground 8). */}
              {arrears_balance > 0 && (() => {
                const regime = data.legislation_regime || 'pre_may_2026';
                const thresholdMonths = regime === 'post_may_2026' ? 3 : 2;
                const months = months_in_arrears || 0;
                const monthsStr = months.toFixed(1);
                const amt = formatCurrency(arrears_balance);
                const atThreshold = months >= thresholdMonths;
                let level, body, note;
                if (atThreshold) {
                  level = 'red';
                  body = `This tenant has reached the Section 8 threshold with ${monthsStr} months arrears totalling ${amt}. You may be eligible to serve a Section 8 notice. Review the compliance checklist before proceeding.`;
                  note = regime === 'post_may_2026'
                    ? 'Ground 8 under the Renters Rights Act 2026 requires at least 3 months arrears.'
                    : 'Ground 8 requires at least 2 months arrears at the date of notice and at the hearing.';
                } else if (months < 1) {
                  level = 'amber';
                  body = `This tenant has an outstanding balance of ${amt}. Consider sending a payment reminder via WhatsApp or email.`;
                  note = null;
                } else {
                  level = 'amber';
                  body = `This tenant is ${monthsStr} months in arrears totalling ${amt}. A formal reminder has been sent. Monitor closely and consider whether further action is needed.`;
                  note = null;
                }
                return (
                  <div className={`arrears-banner arrears-banner-${level}`} data-testid="arrears-guidance-banner">
                    <p className="arrears-banner-body" data-testid="arrears-banner-body">{body}</p>
                    {note && <p className="arrears-banner-note" data-testid="arrears-banner-note">{note}</p>}
                    <p className="arrears-banner-disclaimer">This is not legal advice. If you are unsure of your legal position please consult a solicitor.</p>
                  </div>
                );
              })()}

              <div className="td-section">
                <div className="td-section-header">
                  <h3>Tenancy Details</h3>
                  {!editingTenancy ? (
                    <button className="btn-sm btn-secondary" onClick={() => setEditingTenancy(true)} data-testid="btn-edit-tenancy">Edit</button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn-sm" style={{ background: savingTenancy ? '#94a3b8' : '#0073EA', color: '#fff', border: 'none', cursor: savingTenancy ? 'not-allowed' : 'pointer' }} onClick={handleSaveTenancy} disabled={savingTenancy} data-testid="btn-save-tenancy">{savingTenancy ? 'Saving...' : 'Save'}</button>
                      <button className="btn-sm btn-secondary" onClick={() => setEditingTenancy(false)} data-testid="btn-cancel-tenancy">Cancel</button>
                    </div>
                  )}
                </div>
                <div className="td-fields-grid">
                  <div className="td-field">
                    <label>Rent Amount</label>
                    {editingTenancy ? (
                      <input type="number" value={tenancyForm.rent_amount} onChange={e => setTenancyForm(p => ({ ...p, rent_amount: parseFloat(e.target.value) || '' }))} data-testid="input-rent-amount" />
                    ) : (
                      <span data-testid="display-rent-amount">{tenancy.rent_amount ? formatCurrency(tenancy.rent_amount) : 'N/A'}</span>
                    )}
                  </div>
                  <div className="td-field">
                    <label>Frequency</label>
                    {editingTenancy ? (
                      <select value={tenancyForm.rent_frequency} onChange={e => setTenancyForm(p => ({ ...p, rent_frequency: e.target.value }))} data-testid="input-rent-frequency">
                        <option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
                      </select>
                    ) : (
                      <span>{tenancy.rent_frequency || 'monthly'}</span>
                    )}
                  </div>
                  <div className="td-field">
                    <label>Due Day</label>
                    {editingTenancy ? (
                      <input type="number" min="1" max="31" value={tenancyForm.rent_due_day} onChange={e => setTenancyForm(p => ({ ...p, rent_due_day: parseInt(e.target.value) || 1 }))} data-testid="input-due-day" />
                    ) : (
                      <span>{tenancy.rent_due_day || 1}</span>
                    )}
                  </div>
                  <div className="td-field">
                    <label>Deposit</label>
                    {editingTenancy ? (
                      <input type="number" value={tenancyForm.deposit_amount} onChange={e => setTenancyForm(p => ({ ...p, deposit_amount: parseFloat(e.target.value) || '' }))} data-testid="input-deposit" />
                    ) : (
                      <span>{tenancy.deposit_amount ? formatCurrency(tenancy.deposit_amount) : 'N/A'}</span>
                    )}
                  </div>
                  <div className="td-field">
                    <label>Deposit Scheme</label>
                    {editingTenancy ? (
                      <input type="text" value={tenancyForm.deposit_scheme} onChange={e => setTenancyForm(p => ({ ...p, deposit_scheme: e.target.value }))} data-testid="input-deposit-scheme" />
                    ) : (
                      <span>{tenancy.deposit_scheme || 'Not specified'}</span>
                    )}
                  </div>
                  <div className="td-field">
                    <label>Deposit Certificate</label>
                    {editingTenancy ? (
                      <select value={tenancyForm.deposit_certificate_served ? 'true' : 'false'} onChange={e => setTenancyForm(p => ({ ...p, deposit_certificate_served: e.target.value === 'true' }))} data-testid="input-deposit-cert">
                        <option value="true">Served</option><option value="false">Not served</option>
                      </select>
                    ) : (
                      <span className={tenancy.deposit_certificate_served ? 'text-green' : 'text-red'}>{tenancy.deposit_certificate_served ? 'Served' : 'Not served'}</span>
                    )}
                  </div>
                </div>
                <div className="td-tenancy-actions">
                  <button className="btn-sm btn-danger" onClick={handleEndTenancy} data-testid="btn-end-tenancy">End Tenancy</button>
                </div>
              </div>

              {/* Deposit Dispute Section */}
              {(disputeData || (tenancy && tenancy.deposit_dispute_status && tenancy.deposit_dispute_status !== 'none')) && (
                <div
                  className={`td-section dd-section${tenancy && tenancy.deposit_dispute_status && tenancy.deposit_dispute_status !== 'none' ? ' dd-section-hoisted' : ''}`}
                  data-testid="deposit-dispute-section"
                >
                  <div className="td-section-header">
                    <h3>Deposit Dispute</h3>
                  </div>

                  {disputeData ? (
                    <div className="dd-content">
                      {/* Stage banner — prominent guidance driven by current
                          dispute stage. Never replaces the progress indicator,
                          Advance button, documents list or transitions; those
                          are rendered below unchanged. */}
                      {(() => {
                        const status = disputeData.status;
                        const amt = formatCurrency(disputeData.deposit_amount);
                        const raisedAt = tenancy?.deposit_dispute_raised_at;
                        const daysOpen = raisedAt ? Math.max(0, Math.floor((Date.now() - new Date(raisedAt).getTime()) / 86400000)) : null;
                        const STAGE_LABEL = { raised: 'Raised', evidence_submitted: 'Evidence Submitted', adjudicating: 'Adjudicating', resolved: 'Resolved' };
                        let level = 'amber';
                        let body = null;
                        if (status === 'raised') {
                          level = 'amber';
                          body = (<>
                            <p>A deposit dispute has been raised for <strong>{amt}</strong>. You need to log in to your deposit scheme portal and submit your evidence. You will need your check-in inventory, check-out report, photographs, and any invoices. Return here and mark Evidence Submitted when done.</p>
                            <p className="dd-banner-sub">Scheme portals: TDS (tenancydepositscheme.com), DPS (depositprotection.com), MyDeposits (mydeposits.co.uk).</p>
                          </>);
                        } else if (status === 'evidence_submitted') {
                          level = 'blue';
                          body = (<p>Your evidence has been submitted. The adjudicator will review both parties' submissions. This typically takes 4 to 8 weeks. Return here and mark Adjudicating when the scheme confirms they are reviewing.</p>);
                        } else if (status === 'adjudicating') {
                          level = 'blue';
                          body = (<p>The scheme is reviewing the dispute. Do not contact the tenant directly about the deposit during this period. When the adjudicator issues their decision return here and record the outcome.</p>);
                        } else if (status === 'resolved') {
                          level = 'green';
                          body = (<p>This dispute has been resolved. Outcome: <strong>{disputeData.outcome || 'Not recorded'}</strong>.</p>);
                        }
                        return (
                          <div className={`dd-banner dd-banner-${level}`} data-testid="dd-stage-banner">
                            <div className="dd-banner-header">
                              <span className="dd-banner-stage" data-testid="dd-banner-stage">{STAGE_LABEL[status] || status}</span>
                              <span className="dd-banner-amount" data-testid="dd-banner-amount">{amt}</span>
                              {daysOpen !== null && (
                                <span className="dd-banner-days" data-testid="dd-banner-days">{daysOpen} day{daysOpen === 1 ? '' : 's'} open</span>
                              )}
                            </div>
                            {body}
                            <p className="dd-banner-disclaimer">This is general information only, not legal advice. For complex disputes consider consulting a solicitor or contacting your deposit scheme directly.</p>
                          </div>
                        );
                      })()}

                      {/* Progress indicator */}
                      <div className="dd-progress" data-testid="dd-progress">
                        {['none', 'raised', 'evidence_submitted', 'adjudicating', 'resolved'].map((s, i) => (
                          <div key={s} className={`dd-stage-dot ${i <= disputeData.stage_index ? 'dd-active' : ''} ${s === disputeData.status ? 'dd-current' : ''}`}>
                            <span className="dd-dot-num">{i + 1}</span>
                            <span className="dd-dot-label">{s === 'evidence_submitted' ? 'Evidence' : s === 'none' ? 'None' : s.charAt(0).toUpperCase() + s.slice(1)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="dd-info-row">
                        <span>Deposit: <strong>{formatCurrency(disputeData.deposit_amount)}</strong></span>
                        <span>Scheme: <strong>{disputeData.deposit_scheme || 'Not specified'}</strong></span>
                        <span>Status: <strong className="dd-status-label">{disputeData.status?.replace('_', ' ')}</strong></span>
                      </div>

                      {/* Guidance */}
                      {disputeData.guidance && (
                        <div className="dd-guidance" data-testid="dd-guidance">
                          <p>{disputeData.guidance}</p>
                        </div>
                      )}

                      {/* Outcome fields (when adjudicating → resolved) */}
                      {disputeData.status === 'adjudicating' && (
                        <div className="dd-outcome-form" data-testid="dd-outcome-form">
                          <div className="mf-field">
                            <label>Outcome Description</label>
                            <textarea value={disputeOutcome} onChange={e => setDisputeOutcome(e.target.value)} rows={3} placeholder="Describe the adjudicator's decision..." data-testid="dd-outcome-text" />
                          </div>
                          <div className="mf-row">
                            <div className="mf-field">
                              <label>Amount Disputed</label>
                              <input type="text" value={formatCurrency(disputeData.deposit_amount)} disabled />
                            </div>
                            <div className="mf-field">
                              <label>Amount Awarded</label>
                              <input type="number" step="0.01" value={disputeAmountAwarded} onChange={e => setDisputeAmountAwarded(e.target.value)} placeholder="0.00" data-testid="dd-amount-awarded" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Resolved outcome display */}
                      {disputeData.status === 'resolved' && disputeData.outcome && (
                        <div className="dd-resolved" data-testid="dd-resolved">
                          <p><strong>Outcome:</strong> {disputeData.outcome}</p>
                          {disputeData.amount_awarded != null && <p><strong>Amount Awarded:</strong> {formatCurrency(disputeData.amount_awarded)}</p>}
                        </div>
                      )}

                      {/* Documents */}
                      <div className="dd-docs" data-testid="dd-documents">
                        <h4>Evidence Documents</h4>
                        {disputeData.documents && disputeData.documents.length > 0 ? (
                          <div className="dd-doc-list">
                            {disputeData.documents.map(d => (
                              <div key={d.id} className="dd-doc-item">
                                <span className="dd-doc-name">{d.filename}</span>
                                <span className="dd-doc-date">{formatDate(d.upload_date, '')}</span>
                                <a href={`${API_BASE_URL}${d.download_url}`} target="_blank" rel="noopener noreferrer" className="btn-sm btn-secondary" data-testid={`dd-doc-dl-${d.id}`}>Download</a>
                              </div>
                            ))}
                          </div>
                        ) : <p className="dd-no-docs" data-testid="dd-no-docs">No evidence documents uploaded yet. Upload evidence documents below.</p>}
                        {disputeData.status !== 'resolved' && (
                          <div className="dd-upload">
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={e => handleDisputeUpload(e.target.files)} disabled={disputeUploading} data-testid="dd-file-upload" />
                            {disputeUploading && <span className="dd-uploading">Uploading...</span>}
                          </div>
                        )}
                      </div>

                      {/* Stage transitions history */}
                      {disputeData.transitions && disputeData.transitions.length > 0 && (
                        <div className="dd-history" data-testid="dd-history">
                          <h4>Stage History</h4>
                          {disputeData.transitions.map((t, i) => (
                            <div key={i} className="dd-history-item">
                              <span className="dd-hist-stages">{t.from_stage} &rarr; {t.to_stage}</span>
                              <span className="dd-hist-actor">{t.actor}</span>
                              <span className="dd-hist-date">{formatDateTime(t.transitioned_at, '')}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Advance button */}
                      {disputeData.next_button_label && disputeData.status !== 'resolved' && (
                        <button
                          style={{
                            background: (disputeAdvancing || (disputeData.status === 'adjudicating' && !disputeOutcome)) ? '#94a3b8' : '#0073EA',
                            color: '#fff', border: 'none',
                            cursor: (disputeAdvancing || (disputeData.status === 'adjudicating' && !disputeOutcome)) ? 'not-allowed' : 'pointer',
                            padding: '0.5rem 1rem', borderRadius: 4,
                          }}
                          onClick={handleAdvanceDispute}
                          disabled={disputeAdvancing || (disputeData.status === 'adjudicating' && !disputeOutcome)}
                          data-testid="btn-advance-dispute">
                          {disputeAdvancing ? 'Processing...' : disputeData.next_button_label}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p>Loading dispute data...</p>
                  )}
                </div>
              )}

              {/* Property Landlord */}
              {property && (
                <div className="td-section" data-testid="property-landlord-section">
                  <div className="td-section-header">
                    <h3>Property Landlord</h3>
                  </div>
                  <div className="td-landlord-selector">
                    <div className="td-field">
                      <label>Linked Landlord</label>
                      <select
                        value={propertyLandlord?._id || ''}
                        onChange={e => handleLandlordChange(e.target.value)}
                        disabled={savingLandlord}
                        data-testid="select-property-landlord"
                      >
                        <option value="">— No landlord linked —</option>
                        {allLandlords.map(ll => (
                          <option key={ll._id} value={ll._id}>
                            {ll.full_name}{ll.company_name ? ` (${ll.company_name})` : ''}
                          </option>
                        ))}
                      </select>
                      {savingLandlord && <span className="td-saving-indicator">Saving...</span>}
                    </div>
                    {propertyLandlord && (
                      <div className="td-landlord-detail" data-testid="landlord-detail-card">
                        <div className="td-landlord-row"><span className="td-ll-label">Name</span><span>{propertyLandlord.full_name}</span></div>
                        {propertyLandlord.company_name && <div className="td-landlord-row"><span className="td-ll-label">Company</span><span>{propertyLandlord.company_name}</span></div>}
                        <div className="td-landlord-row"><span className="td-ll-label">Email</span><span>{propertyLandlord.email}</span></div>
                        <div className="td-landlord-row"><span className="td-ll-label">Phone</span><span>{propertyLandlord.phone_number}</span></div>
                        <div className="td-landlord-row">
                          <span className="td-ll-label">PRS Database</span>
                          <span className={propertyLandlord.prs_database_registered ? 'text-green' : 'text-red'} data-testid="landlord-prs-status">
                            {propertyLandlord.prs_database_registered ? 'Registered' : 'Not Registered'}
                          </span>
                        </div>
                      </div>
                    )}
                    {!propertyLandlord && (
                      <div className="td-landlord-warning" data-testid="no-landlord-warning">
                        No landlord linked to this property. A landlord must be assigned before a Section 8 case can proceed past Stage 2.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Property Licensing */}
              {property && licensing && (
                <div className="td-section" data-testid="property-licensing-section">
                  <div className="td-section-header">
                    <h3>Property Licensing</h3>
                    {savingLicensing && <span className="td-saving-indicator">Saving...</span>}
                  </div>

                  {/* HMO Subsection */}
                  <div className="td-lic-subsection" data-testid="hmo-subsection">
                    <div className="td-lic-header">
                      <span className="td-lic-title">HMO (House in Multiple Occupation)</span>
                      <button
                        className={`toggle-btn ${licensing.is_hmo ? 'toggle-on' : 'toggle-off'}`}
                        onClick={() => updateLicensingField('is_hmo', !licensing.is_hmo)}
                        data-testid="toggle-is-hmo"
                      >
                        {licensing.is_hmo ? 'YES' : 'NO'}
                      </button>
                      {licensing.is_hmo && <span className={`lic-badge ${getLicenceStatus(licensing.hmo_licence_expiry).cls}`} data-testid="hmo-status-badge">{getLicenceStatus(licensing.hmo_licence_expiry).label}</span>}
                    </div>
                    {licensing.is_hmo && (
                      <div className="td-lic-fields">
                        <div className="td-field">
                          <label>Licence Number</label>
                          <input type="text" value={licensing.hmo_licence_number || ''} onChange={e => updateLicensingField('hmo_licence_number', e.target.value)} placeholder="HMO-12345" data-testid="input-hmo-number" />
                        </div>
                        <div className="td-field">
                          <label>Issuing Council</label>
                          <input type="text" value={licensing.hmo_licence_issuing_council || ''} onChange={e => updateLicensingField('hmo_licence_issuing_council', e.target.value)} placeholder="Local council name" data-testid="input-hmo-council" />
                        </div>
                        <div className="td-field">
                          <label>Licence Expiry</label>
                          <input type="date" value={licensing.hmo_licence_expiry || ''} onChange={e => updateLicensingField('hmo_licence_expiry', e.target.value)} data-testid="input-hmo-expiry" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selective Licence Subsection */}
                  <div className="td-lic-subsection" data-testid="selective-licence-subsection">
                    <div className="td-lic-header">
                      <span className="td-lic-title">Selective Licence</span>
                      {licensing.selective_licence_number && <span className={`lic-badge ${getLicenceStatus(licensing.selective_licence_expiry).cls}`} data-testid="selective-status-badge">{getLicenceStatus(licensing.selective_licence_expiry).label}</span>}
                    </div>
                    {licensing.selective_licence_number ? (
                      <div className="td-lic-fields">
                        <div className="td-field">
                          <label>Licence Number</label>
                          <input type="text" value={licensing.selective_licence_number || ''} onChange={e => updateLicensingField('selective_licence_number', e.target.value)} data-testid="input-selective-number" />
                        </div>
                        <div className="td-field">
                          <label>Licence Expiry</label>
                          <input type="date" value={licensing.selective_licence_expiry || ''} onChange={e => updateLicensingField('selective_licence_expiry', e.target.value)} data-testid="input-selective-expiry" />
                        </div>
                        <div className="td-field">
                          <button className="btn-sm btn-delete" onClick={() => { updateLicensingField('selective_licence_number', null); updateLicensingField('selective_licence_expiry', null); }}>Remove Selective Licence</button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn-sm btn-edit" onClick={() => updateLicensingField('selective_licence_number', 'NEW')} data-testid="btn-add-selective">Add Selective Licence</button>
                    )}
                  </div>

                  {/* Additional Licence Subsection */}
                  <div className="td-lic-subsection" data-testid="additional-licence-subsection">
                    <div className="td-lic-header">
                      <span className="td-lic-title">Additional Licence</span>
                      {licensing.additional_licence_number && <span className={`lic-badge ${getLicenceStatus(licensing.additional_licence_expiry).cls}`} data-testid="additional-status-badge">{getLicenceStatus(licensing.additional_licence_expiry).label}</span>}
                    </div>
                    {licensing.additional_licence_number ? (
                      <div className="td-lic-fields">
                        <div className="td-field">
                          <label>Licence Number</label>
                          <input type="text" value={licensing.additional_licence_number || ''} onChange={e => updateLicensingField('additional_licence_number', e.target.value)} data-testid="input-additional-number" />
                        </div>
                        <div className="td-field">
                          <label>Licence Expiry</label>
                          <input type="date" value={licensing.additional_licence_expiry || ''} onChange={e => updateLicensingField('additional_licence_expiry', e.target.value)} data-testid="input-additional-expiry" />
                        </div>
                        <div className="td-field">
                          <button className="btn-sm btn-delete" onClick={() => { updateLicensingField('additional_licence_number', null); updateLicensingField('additional_licence_expiry', null); }}>Remove Additional Licence</button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn-sm btn-edit" onClick={() => updateLicensingField('additional_licence_number', 'NEW')} data-testid="btn-add-additional">Add Additional Licence</button>
                    )}
                  </div>
                </div>
              )}

              {/* Property Availability (OpenClaw pre-response Layer) */}
              {property && availability && (
                <div className="td-section" data-testid="property-availability-section">
                  <div className="td-section-header">
                    <h3>Property Availability</h3>
                    <span
                      className={`lic-badge pa-badge-${availabilityForm.availability_status}`}
                      data-testid="availability-status-badge"
                    >
                      {availabilityForm.availability_status === 'available' ? 'Available to Let'
                        : availabilityForm.availability_status === 'under_offer' ? 'Under Offer'
                        : 'Occupied'}
                    </span>
                  </div>
                  <div className="td-lic-fields">
                    <div className="td-field">
                      <label>Availability Status</label>
                      <select
                        value={availabilityForm.availability_status}
                        onChange={e => setAvailabilityForm(f => ({ ...f, availability_status: e.target.value }))}
                        data-testid="select-availability-status"
                      >
                        <option value="occupied">Occupied</option>
                        <option value="available">Available to Let</option>
                        <option value="under_offer">Under Offer</option>
                      </select>
                    </div>
                    <div className="td-field">
                      <label>Asking Rent (£ per month)</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={availabilityForm.asking_rent}
                        onChange={e => setAvailabilityForm(f => ({ ...f, asking_rent: e.target.value }))}
                        placeholder="e.g. 1850"
                        data-testid="input-asking-rent"
                      />
                    </div>
                    <div className="td-field">
                      <label>Available From</label>
                      <input
                        type="date"
                        value={availabilityForm.available_from || ''}
                        onChange={e => setAvailabilityForm(f => ({ ...f, available_from: e.target.value }))}
                        data-testid="input-available-from"
                      />
                    </div>
                    <div className="td-field td-field-wide">
                      <label>Listing Description</label>
                      <textarea
                        rows={3}
                        value={availabilityForm.listing_description}
                        onChange={e => setAvailabilityForm(f => ({ ...f, listing_description: e.target.value }))}
                        placeholder="Short description Estatel AI AI will share with prospective tenants when the property is available."
                        data-testid="textarea-listing-description"
                      />
                    </div>
                    <div className="td-field">
                      <button
                        className="btn-sm"
                        style={{
                          background: savingAvailability ? '#94a3b8' : '#0073EA',
                          color: '#fff', border: 'none',
                          cursor: savingAvailability ? 'not-allowed' : 'pointer',
                        }}
                        disabled={savingAvailability}
                        onClick={saveAvailability}
                        data-testid="btn-save-availability"
                      >
                        {savingAvailability ? 'Saving...' : 'Save Availability'}
                      </button>
                    </div>
                  </div>

                  {availabilityInterests && availabilityInterests.length > 0 && (
                    <div className="td-lic-subsection" data-testid="availability-interests-list">
                      <div className="td-lic-header">
                        <span className="td-lic-title">Registered Interest ({availabilityInterests.length})</span>
                      </div>
                      <table className="td-table">
                        <thead>
                          <tr><th>Contact</th><th>Method</th><th>Value</th><th>Message</th><th>Received</th></tr>
                        </thead>
                        <tbody>
                          {availabilityInterests.map((it) => (
                            <tr key={it._id} data-testid={`availability-interest-row-${it._id}`}>
                              <td className="cell-bold">{it.contact_name}</td>
                              <td>{it.contact_method}</td>
                              <td>{it.contact_value}</td>
                              <td>{it.original_message || ''}</td>
                              <td>{timeAgo(it.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Arrears History — FIX 4: single Log Rent button + 4-column table */}
              <div className="td-section">
                <div className="td-section-header">
                  <h3>Arrears History</h3>
                  <button
                    className="btn-sm"
                    style={{ background: '#0073EA', color: '#fff', border: 'none', cursor: 'pointer' }}
                    onClick={() => {
                      setLogRentError(null);
                      setLogRentResult(null);
                      setLogRentForm({
                        rent_due_date: '',
                        amount_due: tenancy?.rent_amount != null ? String(tenancy.rent_amount) : '',
                        amount_paid: '0',
                        payment_date: '',
                      });
                      setShowLogRentModal(true);
                    }}
                    data-testid="btn-log-rent">
                    Log Rent
                  </button>
                </div>
                <div className="td-arrears-total" data-testid="arrears-total">
                  Total Balance: <strong className={arrears_balance > 0 ? 'text-red' : 'text-green'}>{arrears_balance > 0 ? formatCurrency(arrears_balance) : formatCurrency(0)}</strong>
                </div>
                <table className="td-table" data-testid="arrears-table">
                  <thead><tr><th>Rent Due Date</th><th>Amount Due</th><th>Amount Paid</th><th>Balance Outstanding</th><th>Payment Date</th></tr></thead>
                  <tbody>
                    {(!arrears_records || arrears_records.length === 0) ? (
                      <tr><td colSpan="5" className="cell-empty">No rent periods logged yet.</td></tr>
                    ) : arrears_records.map((r, i) => {
                      // FIX 4 — never display negative balances; clamp to zero.
                      const bal = Math.max(0, parseFloat(r.balance_outstanding || 0));
                      // Use period_start as the rent due date (single date model).
                      const dueDate = r.period_start || r.period_end;
                      return (
                        <tr key={i}>
                          <td>{dueDate ? formatDate(dueDate) : 'N/A'}</td>
                          <td>{formatCurrency(r.amount_due)}</td>
                          <td>{formatCurrency(r.amount_paid)}</td>
                          <td className={bal > 0 ? 'text-red' : 'text-green'}>{formatCurrency(bal)}</td>
                          <td>{r.payment_date ? formatDate(r.payment_date) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {arrears_balance > 0 && (() => {
                  const owingPeriods = (arrears_records || []).filter(r => parseFloat(r.balance_outstanding || 0) > 0).length;
                  const paidRecords = (arrears_records || []).filter(r => parseFloat(r.amount_paid || 0) > 0);
                  let lastPayment = null;
                  paidRecords.forEach(r => {
                    const d = r.payment_date || r.period_end || r.period_start;
                    if (d && (!lastPayment || d > lastPayment)) lastPayment = d;
                  });
                  return (
                    <div className="arrears-plain-english" data-testid="arrears-plain-english">
                      <strong>What this means</strong> — your tenant owes {formatCurrency(arrears_balance)} across {owingPeriods} payment period{owingPeriods === 1 ? '' : 's'}. Their last recorded payment was {lastPayment ? formatDate(lastPayment) : 'not recorded'}. Use the Log Rent button to record each rent period as it falls due.
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB 3: Case */}
      {activeTab === 'case' && hasCase && (
        <div className="td-tab-content" data-testid="case-tab">
          <div className="td-section">
            <h3>Section 8 Case — Stage {caseStage}: {STAGE_LABELS[caseStage] || 'Unknown'}</h3>
            {/* Progress bar */}
            <div className="td-progress-bar" data-testid="case-progress">
              {[1,2,3,4,5,6,7,8].map(s => (
                <div key={s} className={`td-progress-step ${s < caseStage ? 'done' : s === caseStage ? 'current' : ''}`}>
                  <div className="step-dot">{s}</div>
                  <span className="step-label">{STAGE_LABELS[s]}</span>
                </div>
              ))}
            </div>
            {/* Deadlines */}
            <div className="td-case-details">
              {active_case.notice_period_end && <div className="td-deadline"><span className="dl-label">Notice Period Ends:</span> <strong>{active_case.notice_period_end}</strong></div>}
              {active_case.court_filing_deadline && <div className="td-deadline"><span className="dl-label">Court Filing Deadline:</span> <strong>{active_case.court_filing_deadline}</strong></div>}
              {active_case.hearing_date && <div className="td-deadline td-deadline-highlight"><span className="dl-label">Hearing Date:</span> <strong>{active_case.hearing_date}</strong></div>}
            </div>
            {/* Stage-specific actions */}
            <div className="td-case-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Link to={`/section8/case/${active_case.id || active_case._id}`} style={{ background: '#0073EA', color: '#fff', textDecoration: 'none', padding: '0.5rem 1rem', borderRadius: 4, display: 'inline-block' }} data-testid="btn-view-full-case">View Full Case</Link>
              {/* FIX 5/6 — Start Court Proceedings is always available
                  once service has been recorded. No notice-period block. */}
              {(active_case.service_recorded_at || caseStage >= 5) && (
                <button
                  type="button"
                  onClick={() => navigate(`/section8/case/${active_case.id || active_case._id}`)}
                  data-testid="btn-start-court-proceedings"
                  style={{ background: '#0073EA', color: '#fff', border: 'none',
                           padding: '0.5rem 1rem', borderRadius: 4,
                           cursor: 'pointer', fontWeight: 500 }}>
                  Start Court Proceedings
                </button>
              )}
            </div>
            {/* Transitions log */}
            {case_transitions && case_transitions.length > 0 && (
              <div className="td-transitions">
                <h4>Stage History</h4>
                {case_transitions.map((t, i) => (
                  <div key={i} className="td-transition-row">
                    <span className="tr-stage">Stage {t.from_stage} {'\u2192'} {t.to_stage}</span>
                    <span className="tr-date">{formatDateTime(t.transitioned_at, '')}</span>
                    {t.notes && <span className="tr-notes">{t.notes}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: Compliance */}
      {tenancy && activeTab === 'compliance' && (
        <div className="td-tab-content" data-testid="compliance-tab">
          <div className="td-section">
            <h3>Compliance — {address}</h3>
            {!property ? <div className="empty-state"><p>No property assigned</p></div> : (
              <div className="td-compliance-grid" data-testid="compliance-grid">
                {Object.entries(DOC_TYPE_LABELS).map(([dt, label]) => {
                  const doc = (compliance_docs || []).find(d => d.doc_type === dt);
                  const expiry = doc?.expiry_date;
                  let daysLeft = null;
                  let statusCls = 'comp-missing';
                  if (expiry) {
                    daysLeft = Math.ceil((new Date(expiry) - new Date()) / 86400000);
                    statusCls = daysLeft <= 0 ? 'comp-expired' : daysLeft <= 30 ? 'comp-urgent' : daysLeft <= 90 ? 'comp-warning' : 'comp-ok';
                  }
                  return (
                    <div key={dt} className={`td-comp-card ${statusCls}`} data-testid={`comp-card-${dt}`}>
                      <div className="comp-card-header">
                        <h4>{label}</h4>
                        <span className={`comp-status-dot ${statusCls}`} />
                      </div>
                      {doc ? (
                        <div className="comp-card-body">
                          <p>Expiry: <strong>{expiry}</strong></p>
                          {daysLeft !== null && <p className="comp-days">{daysLeft <= 0 ? 'EXPIRED' : `${daysLeft} days remaining`}</p>}
                          {doc.issuing_company && <p className="comp-issuer">{doc.issuing_company}</p>}
                        </div>
                      ) : (
                        <div className="comp-card-body"><p className="comp-missing-text">Not uploaded</p></div>
                      )}
                      {/* FIX 2 — Upload button on tenant compliance tab.
                          Reuses the existing ComplianceUpload modal pre-
                          filled with this property + doc type. */}
                      <div className="comp-card-actions" style={{ padding: '0.6rem 0.85rem' }}>
                        <button
                          type="button"
                          onClick={() => setComplianceUploadHint({
                            doc_type: dt,
                            property_id: property?.id || property?._id || tenancy?.property_id,
                          })}
                          data-testid={`btn-upload-comp-${dt}`}
                          style={{ background: '#0073EA', color: '#fff',
                                   border: 'none', padding: '0.4rem 0.85rem',
                                   borderRadius: 4, fontSize: '0.82rem',
                                   cursor: 'pointer', fontWeight: 500 }}>
                          {doc ? 'Upload New Version' : 'Upload'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FIX 2 — Compliance upload modal triggered from tenant compliance tab. */}
      {complianceUploadHint && (
        <ComplianceUpload
          initialDocType={complianceUploadHint.doc_type}
          initialPropertyId={complianceUploadHint.property_id}
          onClose={() => setComplianceUploadHint(null)}
          onSaved={() => { setComplianceUploadHint(null); fetchData(); }}
        />
      )}

      {/* Message Composer Slide-in */}
      {showComposer && (
        <div className="composer-overlay" onClick={() => setShowComposer(false)}>
          <div className="composer-panel" onClick={e => e.stopPropagation()} data-testid="message-composer">
            <div className="composer-header">
              <h3>Send Message to {tenant.first_name}</h3>
              <button className="btn-close" onClick={() => setShowComposer(false)} data-testid="btn-close-composer">&times;</button>
            </div>
            <div className="composer-tabs">
              {/* 2026-05-31 — WhatsApp composer tab removed; email-only. */}
              <button className="composer-tab active" data-testid="composer-tab-email">
                Email
              </button>
            </div>
            <div className="composer-body">
              <>
                <div className="form-group">
                  <label>Subject *</label>
                  <input type="text" value={msgSubject} onChange={e => setMsgSubject(e.target.value)} placeholder="Email subject" data-testid="input-subject" />
                </div>
                <div className="form-group">
                  <label>Body *</label>
                  <textarea value={msgBody} onChange={e => setMsgBody(e.target.value)} placeholder="Email body" rows="10" data-testid="input-body" />
                </div>
                <div className="signature-preview"><small>Signature will be appended automatically</small></div>
              </>
            </div>
            <div className="composer-footer">
              <button className="btn-send" style={{ background: sending ? '#94a3b8' : '#0073EA', color: '#fff', border: 'none', cursor: sending ? 'not-allowed' : 'pointer' }} onClick={handleSend} disabled={sending} data-testid="btn-send">
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FIX 6 — Start Court Proceedings notice-period warning overlay */}
      {noticeGate.open && (() => {
        const npe = noticeGate.notice_period_end ? new Date(noticeGate.notice_period_end) : null;
        const days = npe ? Math.max(0, Math.ceil((npe.getTime() - Date.now()) / 86400000)) : null;
        const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB',
          { day: 'numeric', month: 'long', year: 'numeric' }) : '';
        const closeGate = () => setNoticeGate({ open: false, notice_period_end: null, case_id: null });
        const saveReminder = async () => {
          setSavingReminder(true);
          try {
            await fetch(`${API_BASE_URL}/api/section8/notice-period-reminder`, {
              method: 'POST', headers: jsonHeaders,
              body: JSON.stringify({ case_id: noticeGate.case_id }),
            });
          } catch { /* best-effort */ }
          setSavingReminder(false);
          closeGate();
        };
        return (
          <div data-testid="court-gate-overlay"
               style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 200, padding: '1rem' }}
               onClick={(e) => { if (e.target === e.currentTarget) closeGate(); }}>
            <div data-testid="court-gate-modal"
                 style={{ background: '#fff', borderRadius: 10, padding: '1.5rem 1.75rem',
                          width: '100%', maxWidth: 580,
                          boxShadow: '0 12px 36px rgba(15,23,42,0.25)' }}>
              <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem', color: '#7c2d12' }}>
                Notice period not yet expired
              </h2>
              <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '0.92rem' }}>
                Your Section 8 notice period has not yet expired. You cannot
                issue court proceedings until <strong>{fmt(noticeGate.notice_period_end)}</strong>.
              </p>
              <p style={{ margin: '0 0 1rem', color: '#475569', fontSize: '0.92rem' }}>
                There {days === 1 ? 'is' : 'are'} <strong>{days}</strong> day{days === 1 ? '' : 's'} remaining.
                You may prepare your bundle in advance so it is ready to submit
                immediately when the period expires.
              </p>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap',
                            justifyContent: 'flex-end' }}>
                <button type="button"
                        onClick={saveReminder}
                        disabled={savingReminder}
                        data-testid="court-gate-remind"
                        style={{ background: '#fff', color: '#475569',
                                 border: '1px solid #cbd5e1',
                                 padding: '0.55rem 1.1rem', borderRadius: 6,
                                 cursor: savingReminder ? 'not-allowed' : 'pointer' }}>
                  {savingReminder ? 'Saving…' : 'Remind Me When Period Expires'}
                </button>
                <Link to={`/section8/case/${noticeGate.case_id}?prepare_in_advance=1`}
                      onClick={closeGate}
                      data-testid="court-gate-prepare-advance"
                      style={{ background: '#0073EA', color: '#fff',
                               textDecoration: 'none',
                               padding: '0.55rem 1.1rem', borderRadius: 6,
                               fontWeight: 500 }}>
                  Start Court Proceedings
                </Link>
              </div>
            </div>
          </div>
        );
      })()}

      {/* FIX 4 — Log Rent modal */}
      {showLogRentModal && (
        <div data-testid="log-rent-modal-overlay"
             style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 100, padding: '1rem' }}
             onClick={(e) => { if (e.target === e.currentTarget) setShowLogRentModal(false); }}>
          <div data-testid="log-rent-modal"
               style={{ background: '#fff', borderRadius: 10,
                        padding: '1.4rem 1.6rem', width: '100%', maxWidth: 540,
                        boxShadow: '0 10px 30px rgba(15,23,42,0.2)' }}>
            <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.15rem' }}>Log Rent</h2>
            <p style={{ margin: '0 0 1rem', color: '#64748b', fontSize: '0.88rem' }}>
              Record one rent period that has fallen due. Balance outstanding
              is amount due minus amount paid (clamped at zero).
            </p>
            {logRentResult ? (
              <>
                <div style={{ background: '#dcfce7', border: '1px solid #86efac',
                              color: '#166534', padding: '0.7rem 0.95rem',
                              borderRadius: 6, marginBottom: '0.85rem' }}
                     data-testid="log-rent-confirm">
                  Rent period logged. Balance outstanding:{' '}
                  <strong>{formatCurrency(logRentResult.balance_outstanding)}</strong>.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button type="button"
                          onClick={() => { setShowLogRentModal(false); setLogRentResult(null); }}
                          data-testid="log-rent-close"
                          style={{ background: '#0073EA', color: '#fff', border: 'none',
                                   padding: '0.55rem 1.1rem', borderRadius: 6, cursor: 'pointer' }}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                              gap: '0.75rem', marginBottom: '0.65rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem',
                                    color: '#475569', marginBottom: '0.2rem' }}>
                      Rent due date
                    </label>
                    <input type="date" value={logRentForm.rent_due_date}
                           onChange={e => setLogRentForm(p => ({ ...p, rent_due_date: e.target.value }))}
                           data-testid="lr-rent-due-date"
                           style={{ width: '100%', padding: '0.5rem',
                                    border: '1px solid #cbd5e1', borderRadius: 4 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem',
                                    color: '#475569', marginBottom: '0.2rem' }}>
                      Amount due (£)
                    </label>
                    <input type="number" step="0.01" min="0"
                           value={logRentForm.amount_due}
                           onChange={e => setLogRentForm(p => ({ ...p, amount_due: e.target.value }))}
                           data-testid="lr-amount-due"
                           style={{ width: '100%', padding: '0.5rem',
                                    border: '1px solid #cbd5e1', borderRadius: 4 }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                              gap: '0.75rem', marginBottom: '0.85rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem',
                                    color: '#475569', marginBottom: '0.2rem' }}>
                      Amount paid (£)
                    </label>
                    <input type="number" step="0.01" min="0"
                           value={logRentForm.amount_paid}
                           onChange={e => setLogRentForm(p => ({ ...p, amount_paid: e.target.value }))}
                           data-testid="lr-amount-paid"
                           style={{ width: '100%', padding: '0.5rem',
                                    border: '1px solid #cbd5e1', borderRadius: 4 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem',
                                    color: '#475569', marginBottom: '0.2rem' }}>
                      Payment date <span style={{ color: '#94a3b8' }}>(optional)</span>
                    </label>
                    <input type="date" value={logRentForm.payment_date}
                           onChange={e => setLogRentForm(p => ({ ...p, payment_date: e.target.value }))}
                           data-testid="lr-payment-date"
                           style={{ width: '100%', padding: '0.5rem',
                                    border: '1px solid #cbd5e1', borderRadius: 4 }} />
                  </div>
                </div>
                {logRentError && (
                  <div data-testid="lr-error"
                       style={{ color: '#dc2626', fontSize: '0.85rem',
                                marginBottom: '0.65rem' }}>
                    {logRentError}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button type="button"
                          onClick={() => setShowLogRentModal(false)}
                          data-testid="lr-cancel"
                          style={{ background: '#fff', color: '#475569',
                                   border: '1px solid #cbd5e1',
                                   padding: '0.55rem 1.1rem', borderRadius: 6,
                                   cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button type="button"
                          onClick={handleLogRent}
                          disabled={loggingRent}
                          data-testid="lr-submit"
                          style={{ background: loggingRent ? '#94a3b8' : '#0073EA',
                                   color: '#fff', border: 'none',
                                   padding: '0.55rem 1.1rem', borderRadius: 6,
                                   cursor: loggingRent ? 'not-allowed' : 'pointer',
                                   fontWeight: 500 }}>
                    {loggingRent ? 'Saving…' : 'Confirm'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Step 52 BUG 2 — Create Tenancy modal. Used when an agent has manually
// added a tenant (no property link) and now needs to attach a tenancy to make
// the rest of the tenant detail page (Log Payment, Start S8, etc.) functional.
function CreateTenancyModal({ tenantId, apiBase, onClose, onCreated }) {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [rentAmount, setRentAmount] = useState('');
  const [rentFrequency, setRentFrequency] = useState('monthly');
  const [rentDueDay, setRentDueDay] = useState('1');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositScheme, setDepositScheme] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/properties`, { credentials: 'include' });
        const txt = await res.text();
        const data = txt ? JSON.parse(txt) : [];
        setProperties(Array.isArray(data) ? data : []);
      } catch (e) { /* keep empty */ }
    })();
  }, [apiBase]);

  const submit = async (e) => {
    e.preventDefault();
    if (!propertyId || !startDate || !rentAmount) {
      return setError('Property, start date and rent amount are required.');
    }
    setSubmitting(true); setError(null);
    try {
      const payload = {
        property_id: propertyId,
        start_date: startDate,
        rent_amount: parseFloat(rentAmount),
        rent_frequency: rentFrequency,
        rent_due_day: parseInt(rentDueDay, 10) || 1,
        deposit_amount: depositAmount ? parseFloat(depositAmount) : null,
        deposit_scheme: depositScheme.trim() || null,
      };
      const res = await fetch(`${apiBase}/api/tenants/${tenantId}/tenancy`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to create tenancy');
        return;
      }
      onCreated && onCreated(data.tenancy_id);
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally { setSubmitting(false); }
  };

  // Step 53 BUG 2 — close on Escape + click-outside, and support an X button.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      data-testid="create-tenancy-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: '1.75rem', width: '520px', maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button
          type="button"
          aria-label="Close"
          data-testid="ct-close-x"
          onClick={onClose}
          disabled={submitting}
          style={{
            position: 'absolute', top: '0.6rem', right: '0.6rem',
            background: 'transparent', border: 'none', fontSize: '1.4rem',
            color: '#64748b', cursor: 'pointer', lineHeight: 1, padding: '0.25rem 0.5rem',
          }}>
          ×
        </button>
        <h3 style={{ marginTop: 0 }}>Create Tenancy</h3>
        {/* 2026-05-06 (Item 4 Part A) — workflow guidance for new agents.
            Tells the agent the prerequisites (landlord + property) and
            offers two inline links that hop to the right Settings/
            Properties surfaces via the App's URL deep-link handler. */}
        <div
          data-testid="ct-prereq-banner"
          style={{
            background: '#fffbeb', border: '1px solid #fcd34d',
            color: '#92400e', borderRadius: 8,
            padding: '0.85rem 1rem', margin: '0.25rem 0 1rem',
            fontSize: '0.88rem', lineHeight: 1.5,
          }}>
          Before creating a tenancy you will need a property and a
          landlord added to Estatel AI. If you have not done this yet
          please add your landlord first in Settings &gt; Landlords,
          then add a property and link it to the landlord before
          returning here.{' '}
          <a href="/?view=settings&tab=landlords"
             data-testid="ct-link-add-landlord"
             style={{ color: '#7c2d12', fontWeight: 600 }}>
            Add a landlord
          </a>
          {' · '}
          <a href="/?view=properties&action=add"
             data-testid="ct-link-add-property"
             style={{ color: '#7c2d12', fontWeight: 600 }}>
            Add a property
          </a>
        </div>
        <form onSubmit={submit}>
          <div className="form-group">
            {/* Step 54 BUG 2 — label removed; the dropdown's "— Select a property —"
                placeholder is self-explanatory. */}
            <select value={propertyId} onChange={e => setPropertyId(e.target.value)} data-testid="ct-property" required>
              <option value="">— Select a property —</option>
              {properties.map(p => (
                <option key={p._id || p.id} value={p._id || p.id}>
                  {p.address_line1}{p.city ? `, ${p.city}` : ''}{p.postcode ? ` ${p.postcode}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tenancy Start Date *</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="ct-start-date" required />
          </div>
          <div className="form-group">
            <label>Rent Amount (£) *</label>
            <input type="number" step="0.01" min="0" value={rentAmount} onChange={e => setRentAmount(e.target.value)} data-testid="ct-rent-amount" required />
          </div>
          <div className="form-group">
            <label>Rent Frequency</label>
            <select value={rentFrequency} onChange={e => setRentFrequency(e.target.value)} data-testid="ct-rent-frequency">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <div className="form-group">
            <label>Rent Due Day (1–31)</label>
            <input type="number" min="1" max="31" value={rentDueDay} onChange={e => setRentDueDay(e.target.value)} data-testid="ct-rent-due-day" />
          </div>
          <div className="form-group">
            <label>Deposit Amount (£)</label>
            <input type="number" step="0.01" min="0" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} data-testid="ct-deposit-amount" />
          </div>
          <div className="form-group">
            <label>Deposit Scheme</label>
            <input type="text" value={depositScheme} onChange={e => setDepositScheme(e.target.value)} placeholder="DPS / mydeposits / TDS" data-testid="ct-deposit-scheme" />
          </div>
          {error && (
            <div role="alert" style={{ background: '#fef2f2', color: '#991b1b', padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.88rem', marginTop: '0.5rem' }} data-testid="ct-error">
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem' }}>
            <button type="button" onClick={onClose} disabled={submitting}
              style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
              data-testid="ct-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              style={{ background: submitting ? '#94a3b8' : '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 500 }}
              data-testid="ct-save">
              {submitting ? 'Creating…' : 'Create Tenancy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TenantDetail;
